import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, EffPill, KV, Pill } from "@/components/controls/ui";
import { NONE, Pick } from "@/components/vendors/NewVendorDialog";
import { api } from "@/lib/api";
import { fmtD, fmtDT } from "@/lib/platform";
import { hasPerm, lbl, run, useControlMeta } from "@/lib/controls";

export const OverviewTab = ({ c, meta }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ws-overview">
    <Card title="Control" className="lg:col-span-2">
      <KV k="Objective" v={c.control_objective} testId="ov-objective" />
      <KV k="Description" v={c.control_description} />
      <KV k="Domain" v={c.domain_label} />
      <KV k="Type / nature" v={`${meta?.control_types?.[c.control_type] || "-"} · ${meta?.natures?.[c.control_nature] || "-"}`} />
      <KV k="Implementation" v={meta?.implementation_methods?.[c.implementation_method]} />
      <KV k="Frequency" v={`${c.frequency_label}${c.execution_schedule_json?.recurring ? ` + ${lbl(c.execution_schedule_json.recurring)}` : ""}`} />
      <KV k="Event triggers" v={(c.execution_schedule_json?.events || []).map((e) => meta?.events?.[e]).join(", ") || "-"} />
      <KV k="Business unit" v={c.business_unit} />
      <KV k="Effective from" v={fmtD(c.effective_from)} />
    </Card>
    <Card title="Ownership and operation">
      <KV k="Accountable owner" v={c.owner_name} testId="ov-owner" />
      <KV k="Operator" v={c.operator_name} />
      <KV k="Reviewer" v={c.reviewer_name} />
      <KV k="Last execution" v={fmtDT(c.last_operated_at)} />
      <KV k="Next due" v={fmtD(c.next_due_at)} testId="ov-next-due" />
      <KV k="Last tested" v={fmtDT(c.last_tested_at)} />
      <KV k="Next test due" v={fmtD(c.next_test_due_at)} />
      <KV k="Effectiveness" v={<EffPill s={c.effectiveness_status} testId="ov-effectiveness" />} />
    </Card>
    <Card title="Related App A items (read-only)" className="lg:col-span-3">
      {c.mappings.filter((m) => m.mapping_type.startsWith("APP_A")).map((m) => (
        <div key={m.id} className="text-sm py-1 flex gap-2 items-center"><Pill cls="bg-slate-50 border-slate-200 text-slate-600">{lbl(m.mapping_type)}</Pill>{m.label}
          {m.read_only && <span className="text-[11px] text-slate-400">{Object.entries(m.read_only).map(([k, v]) => `${k}: ${v ?? "-"}`).join(" · ")}</span>}</div>))}
      {!c.mappings.some((m) => m.mapping_type.startsWith("APP_A")) && <div className="text-xs text-slate-500">No App A mappings.</div>}
      <div className="text-[11px] text-slate-400 mt-2">App A assessment outcomes remain unchanged until a reassessment; App B tracks operational progress only.</div>
    </Card>
  </div>
);

const STEP = { step: "" };
export const ProcedureTab = ({ c, reload }) => {
  const [versions, setVersions] = useState([]);
  const [edit, setEdit] = useState(null);
  const perms = c.my_permissions;
  const meta = useControlMeta();
  const ownDraft = (p) => meta?.settings?.segregation_of_duties && p.created_by_membership_id && p.created_by_membership_id === meta?.membership_id;
  const load = () => api.get(`/controls/${c.id}/procedures`).then(({ data }) => setVersions(data.procedures));
  useEffect(() => { load(); }, [c.id]); // eslint-disable-line
  const draft = versions.find((p) => ["DRAFT", "REJECTED"].includes(p.approval_status));
  const current = versions.find((p) => p.approval_status === "APPROVED");
  const startEdit = (p) => setEdit({ id: p?.id, approved: p?.approval_status === "APPROVED", procedure_title: p?.procedure_title || `${c.control_name} - procedure`,
    procedure_steps_json: p?.procedure_steps_json?.length ? p.procedure_steps_json : [{ ...STEP }], evidence_guidance: p?.evidence_guidance || "",
    exception_handling_guidance: p?.exception_handling_guidance || "", escalation_guidance: p?.escalation_guidance || "", change_summary: "" });
  const save = async () => {
    const body = { ...edit }; delete body.id; delete body.approved;
    const r = edit.id ? await run(() => api.patch(`/control-procedures/${edit.id}`, body), edit.approved ? "New draft version created (approved version unchanged)" : "Draft saved")
      : await run(() => api.post(`/controls/${c.id}/procedures`, body), "Draft version created");
    if (!r) return;
    setEdit(null); load(); reload();
  };
  const act = async (p, a) => {
    const reason = a === "reject" ? window.prompt("Rejection reason") : "";
    if (a === "reject" && !reason) return;
    await run(() => api.post(`/control-procedures/${p.id}/${a}`, a === "submit" ? undefined : { reason }), `Procedure ${a === "submit" ? "submitted" : a + "d"}`);
    load(); reload();
  };
  const st = (i, v) => setEdit((e) => ({ ...e, procedure_steps_json: e.procedure_steps_json.map((s, j) => (j === i ? { ...s, step: v } : s)) }));
  return (
    <div className="space-y-4" data-testid="ws-procedure">
      {hasPerm(perms, "CONTROL_PROCEDURE_EDIT") && !edit && (
        <div className="flex gap-2">
          {draft ? <Button size="sm" onClick={() => startEdit(draft)} data-testid="edit-draft-procedure">Edit draft v{draft.version_number}</Button>
            : <Button size="sm" onClick={() => startEdit(current)} data-testid="new-procedure-version"><Plus className="w-4 h-4 mr-1" />{current ? "Revise (creates new draft version)" : "Write procedure"}</Button>}
        </div>)}
      {edit && (
        <Card title={edit.approved ? "Revise approved procedure - saving creates a new draft version" : "Procedure draft"} testId="procedure-editor">
          <div className="space-y-2">
            <Input value={edit.procedure_title} onChange={(e) => setEdit({ ...edit, procedure_title: e.target.value })} data-testid="proc-title" />
            {edit.procedure_steps_json.map((s, i) => (
              <div key={i} className="flex gap-2"><span className="text-xs font-mono pt-2.5 w-5">{i + 1}.</span><Input value={s.step} onChange={(e) => st(i, e.target.value)} data-testid={`proc-step-${i}`} />
                <Button size="sm" variant="ghost" onClick={() => setEdit((x) => ({ ...x, procedure_steps_json: x.procedure_steps_json.filter((_, j) => j !== i) }))}><Trash2 className="w-4 h-4" /></Button></div>))}
            <Button size="sm" variant="outline" onClick={() => setEdit((x) => ({ ...x, procedure_steps_json: [...x.procedure_steps_json, { ...STEP }] }))} data-testid="proc-add-step">Add step</Button>
            {[["evidence_guidance", "Evidence instructions"], ["exception_handling_guidance", "Exception handling"], ["escalation_guidance", "Escalation"], ["change_summary", "Change summary"]].map(([k, l]) => (
              <Textarea key={k} rows={2} placeholder={l} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`proc-${k}`} />))}
            <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="proc-save">Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
          </div>
        </Card>)}
      {versions.map((p) => (
        <Card key={p.id} testId={`procedure-v${p.version_number}`} title={<span>v{p.version_number} · {p.procedure_title} <Pill cls="ml-2 bg-slate-50 border-slate-200 text-slate-600">{p.status_label}</Pill></span>}
              action={<div className="flex gap-1">
                {["DRAFT", "REJECTED"].includes(p.approval_status) && hasPerm(perms, "CONTROL_PROCEDURE_EDIT") && <Button size="sm" variant="outline" onClick={() => act(p, "submit")} data-testid={`proc-submit-${p.version_number}`}>Submit for approval</Button>}
                {p.approval_status === "IN_REVIEW" && hasPerm(perms, "CONTROL_PROCEDURE_APPROVE") && (ownDraft(p)
                  ? <><span className="text-[11px] text-amber-700 self-center mr-1" data-testid={`proc-sod-note-${p.version_number}`}>Awaiting another approver (segregation of duties)</span>
                  <Button size="sm" variant="outline" onClick={() => act(p, "reject")} data-testid={`proc-reject-${p.version_number}`}>Withdraw</Button></>
                  : <>
                  <Button size="sm" onClick={() => act(p, "approve")} data-testid={`proc-approve-${p.version_number}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => act(p, "reject")} data-testid={`proc-reject-${p.version_number}`}>Reject</Button></>)}
              </div>}>
          <ol className="list-decimal ml-5 text-sm space-y-0.5">{p.procedure_steps_json.map((s) => <li key={s.sequence}>{s.step}</li>)}</ol>
          <div className="text-[11px] text-slate-500 mt-2">Evidence: {p.evidence_guidance || "-"} · Exceptions: {p.exception_handling_guidance || "-"} · Escalation: {p.escalation_guidance || "-"}</div>
          <div className="text-[11px] text-slate-400 mt-1">{p.approved_at ? `Approved ${fmtDT(p.approved_at)} by ${p.approved_by_name || p.approved_by || "-"} · effective ${fmtD(p.effective_from)}${p.effective_until ? ` - ${fmtD(p.effective_until)}` : ""}` : `Created by ${p.created_by}`}
            {p.sha256_hash && <span className="font-mono"> · sha256 {p.sha256_hash.slice(0, 16)}…</span>} · used by {p.executions_using} executions, {p.tests_using} tests {p.rejection_reason && <span className="text-red-600">· Rejected: {p.rejection_reason}</span>}</div>
        </Card>))}
      {!versions.length && <div className="text-sm text-slate-500">No procedure yet.</div>}
    </div>
  );
};

export const MappingsTab = ({ c, meta, reload }) => {
  const [mt, setMt] = useState("ROPA_ACTIVITY");
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [target, setTarget] = useState(NONE);
  const can = hasPerm(c.my_permissions, "CONTROL_EDIT");
  const freeText = ["OBLIGATION", "BUSINESS_UNIT", "ROPA_DATA_CLASS"].includes(mt);
  useEffect(() => { if (!freeText) api.get("/control-mapping-options", { params: { mapping_type: mt, q } }).then(({ data }) => setOptions(data.options)).catch(() => {}); }, [mt, q, freeText]);
  const add = async () => {
    const isValue = freeText || meta?.value_enums?.[mt];
    if (!await run(() => api.post(`/controls/${c.id}/mappings`, isValue ? { mapping_type: mt, value: freeText ? q : target } : { mapping_type: mt, target_id: target }), "Mapped")) return;
    setTarget(NONE); setQ(""); reload();
  };
  const del = async (m) => { await run(() => api.delete(`/controls/${c.id}/mappings/${m.id}`), "Mapping removed"); reload(); };
  return (
    <div className="space-y-4" data-testid="ws-mappings">
      {can && (
        <Card title="Add mapping">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="w-48"><Pick value={mt} onChange={(v) => { setMt(v); setTarget(NONE); }} options={(meta?.mapping_types || []).map((k) => [k, lbl(k)])} testId="map-type" /></div>
            <Input className="w-56 h-9" placeholder={freeText ? "Value (e.g. DPDP s.8(5))" : "Search"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="map-search" />
            {!freeText && <div className="w-72"><Pick value={target} onChange={setTarget} none="Select" options={options.map((o) => [o.id, `${o.name}${o.detail ? ` · ${String(o.detail).slice(0, 12)}` : ""}`])} testId="map-target" /></div>}
            <Button size="sm" onClick={add} disabled={freeText ? !q : target === NONE} data-testid="map-add">Map</Button>
          </div>
        </Card>)}
      <Card title={`Mappings (${c.mappings.length})`}>
        <table className="w-full text-sm" data-testid="mapping-table"><tbody>
          {c.mappings.map((m) => (
            <tr key={m.id} className="border-b border-slate-50" data-testid={`mapping-${m.id}`}>
              <td className="py-2 w-44"><Pill cls="bg-slate-50 border-slate-200 text-slate-600">{lbl(m.mapping_type)}</Pill></td>
              <td className="py-2 text-[#003366]">{m.label}{!m.live && <span className="ml-2 text-[11px] text-red-600">source record no longer exists (history preserved)</span>}</td>
              <td className="py-2 text-right">{can && <Button size="sm" variant="ghost" onClick={() => del(m)} data-testid={`mapping-del-${m.id}`}><Trash2 className="w-4 h-4" /></Button>}</td>
            </tr>))}
          {!c.mappings.length && <tr><td className="py-4 text-xs text-slate-500">No mappings yet.</td></tr>}
        </tbody></table>
        <div className="text-[11px] text-slate-400 mt-2">Live links show current RoPA/vendor data. Completed tests keep a snapshot of the context used.</div>
      </Card>
    </div>
  );
};

export const AuditTab = ({ c }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/controls/${c.id}/history`).then(({ data }) => setD(data)).catch(() => {}); }, [c.id]);
  return (
    <Card title="Append-only activity timeline" testId="ws-audit">
      <div className="space-y-1.5">
        {d?.events?.map((e) => (
          <div key={e.id} className="text-xs flex gap-3 border-b border-slate-50 pb-1.5">
            <span className="font-mono text-slate-400 w-36 shrink-0">{fmtDT(e.timestamp)}</span><span className="w-32 shrink-0 font-medium">{e.event_label}</span>
            <span className="w-28 shrink-0 text-slate-500">{lbl(e.object_type)} {e.object_label}</span><span className="text-slate-500">{e.actor}</span>
            <span className="text-slate-400 truncate">{e.new_value ? JSON.stringify(e.new_value).slice(0, 140) : ""}</span>
          </div>))}
        {!d?.events?.length && <div className="text-xs text-slate-500">No events.</div>}
      </div>
    </Card>
  );
};

export const RemediationTab = ({ c }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/deficiencies", { params: { control_id: c.id } }).then(async ({ data }) => {
      const out = [];
      for (const d of data.deficiencies) { const r = await api.get(`/deficiencies/${d.id}`); r.data.remediations.forEach((x) => out.push({ ...x, def: d })); }
      setRows(out);
    }).catch(() => {});
  }, [c.id]);
  return (
    <Card title="Linked remediation (existing Remediation module)" testId="ws-remediation">
      <table className="w-full text-sm"><tbody>
        {rows.map((r) => (
          <tr key={r.task_id} className="border-b border-slate-50"><td className="py-2 font-mono text-xs"><Link className="text-[#008080]" to={`/controls/deficiencies/${r.def.id}`}>{r.def.deficiency_reference}</Link></td>
            <td className="py-2">{r.title || r.description}</td><td className="text-xs">{r.status}</td><td className="text-xs font-mono">{fmtD(r.due_date)}</td>
            <td className="text-xs">{r.def.validation?.result ? `Validated: ${r.def.validation.result}` : "Not validated"}</td></tr>))}
        {!rows.length && <tr><td className="py-4 text-xs text-slate-500">No remediation linked to this control's deficiencies.</td></tr>}
      </tbody></table>
      <div className="text-[11px] text-slate-400 mt-2">Closing a remediation item never closes the deficiency - closure requires validation.</div>
    </Card>
  );
};
